import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Card, Button, Input, Textarea, PageHeader, Spinner, EmptyState, Badge, Field, Select, ErrorBanner, formatDateTime } from '../components/ui';

interface Conversation {
  id: string;
  channel: 'sms' | 'email' | 'webchat' | 'rcs' | 'whatsapp';
  status: 'open' | 'closed' | 'snoozed';
  unreadCount: number;
  lastMessageAt: string;
  contactId: string;
  assignedTo?: string;
}

interface Message {
  id: string;
  body: string;
  direction: 'inbound' | 'outbound';
  channel: string;
  createdAt: string;
}

interface Template {
  id: string;
  name: string;
  body: string;
}

export function InboxPage() {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'open' | 'closed'>('open');
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['inbox', filter],
    queryFn: () => {
      const params: Record<string, string> = { limit: '50' };
      if (filter !== 'all') params.status = filter;
      return api.get<{ data: Conversation[] }>('/inbox/conversations', params).catch(() => ({ data: [] }));
    },
  });

  const conversations = q.data?.data ?? [];
  const active = conversations.find((c) => c.id === activeId);

  return (
    <div>
      <PageHeader
        title="Messages"
        subtitle={`${conversations.length} conversations`}
        action={
          <Select
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'open', label: 'Open' },
              { value: 'closed', label: 'Closed' },
              { value: 'all', label: 'All' },
            ]}
          />
        }
      />

      {q.isLoading ? (
        <Spinner />
      ) : conversations.length === 0 ? (
        <Card>
          <EmptyState title="Inbox zero" description="When clients send you SMS, email, or chat messages, they'll show up here." />
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 'var(--space-3)', minHeight: 600 }}>
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {conversations.map((c) => (
                <li
                  key={c.id}
                  onClick={() => setActiveId(c.id)}
                  style={{
                    padding: 'var(--space-3)',
                    borderBottom: '1px solid var(--color-border-primary)',
                    cursor: 'pointer',
                    background: c.id === activeId ? 'var(--color-brand-primary-light)' : 'transparent',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Badge color="blue">{c.channel}</Badge>
                    {c.unreadCount > 0 && <Badge color="red">{c.unreadCount}</Badge>}
                  </div>
                  <div style={{ fontSize: 'var(--text-sm)', marginTop: 4 }}>Contact {c.contactId.slice(0, 8)}</div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                    {formatDateTime(c.lastMessageAt)}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
          <div>
            {active ? <ConversationView conversation={active} onChanged={() => qc.invalidateQueries({ queryKey: ['inbox'] })} /> : (
              <Card>
                <EmptyState title="Pick a conversation" description="Select one from the list." />
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ConversationView({ conversation, onChanged }: { conversation: Conversation; onChanged: () => void }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const [channel, setChannel] = useState<Conversation['channel']>(conversation.channel);
  const [showTemplates, setShowTemplates] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const messagesQ = useQuery({
    queryKey: ['inbox', conversation.id, 'messages'],
    queryFn: () => api.get<{ data: Message[] }>(`/inbox/conversations/${conversation.id}/messages`).catch(() => ({ data: [] })),
  });

  const templatesQ = useQuery({
    queryKey: ['message-templates'],
    queryFn: () => api.get<{ data: Template[] }>('/message-templates').catch(() => ({ data: [] })),
  });

  const sendMut = useMutation({
    mutationFn: () => api.post(`/inbox/conversations/${conversation.id}/messages`, { body: draft, channel }),
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: ['inbox', conversation.id, 'messages'] });
    },
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Send failed'),
  });

  const closeMut = useMutation({
    mutationFn: () => api.post(`/inbox/conversations/${conversation.id}/close`),
    onSuccess: onChanged,
  });

  const assignMut = useMutation({
    mutationFn: (userId: string) => api.post(`/inbox/conversations/${conversation.id}/assign`, { userId }),
    onSuccess: onChanged,
  });

  const messages = messagesQ.data?.data ?? [];
  const templates = templatesQ.data?.data ?? [];

  return (
    <Card style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 600 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--color-border-primary)', paddingBottom: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
        <div>
          <strong>Contact {conversation.contactId.slice(0, 8)}</strong>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
            via {conversation.channel}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <input
            placeholder="Assign to user ID"
            onBlur={(e) => e.target.value && assignMut.mutate(e.target.value)}
            style={{ padding: 'var(--space-1) var(--space-2)', border: '1px solid var(--color-border-primary)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-xs)' }}
          />
          <Button size="sm" variant="secondary" onClick={() => closeMut.mutate()} disabled={closeMut.isPending}>
            Close
          </Button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {messagesQ.isLoading ? (
          <Spinner />
        ) : messages.length === 0 ? (
          <p style={{ color: 'var(--color-text-tertiary)' }}>No messages yet.</p>
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              style={{
                alignSelf: m.direction === 'outbound' ? 'flex-end' : 'flex-start',
                background: m.direction === 'outbound' ? 'var(--color-brand-primary)' : 'var(--color-surface-secondary)',
                color: m.direction === 'outbound' ? 'white' : 'var(--color-text-primary)',
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-lg)',
                maxWidth: '70%',
                fontSize: 'var(--text-sm)',
              }}
            >
              <div style={{ whiteSpace: 'pre-wrap' }}>{m.body}</div>
              <div style={{ fontSize: 10, opacity: 0.7, marginTop: 4 }}>{formatDateTime(m.createdAt)}</div>
            </div>
          ))
        )}
      </div>

      <div style={{ marginTop: 'var(--space-3)', borderTop: '1px solid var(--color-border-primary)', paddingTop: 'var(--space-3)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', alignItems: 'center' }}>
          <Select
            value={channel}
            onChange={setChannel}
            options={[
              { value: 'sms', label: 'SMS' },
              { value: 'email', label: 'Email' },
              { value: 'webchat', label: 'Webchat' },
              { value: 'rcs', label: 'RCS' },
              { value: 'whatsapp', label: 'WhatsApp' },
            ]}
          />
          <Button size="sm" variant="ghost" onClick={() => setShowTemplates(!showTemplates)}>
            Templates
          </Button>
        </div>
        {showTemplates && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)', marginBottom: 'var(--space-2)' }}>
            {templates.length === 0 ? (
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>No templates yet.</p>
            ) : (
              templates.map((t) => (
                <button
                  key={t.id}
                  onClick={() => {
                    setDraft(t.body);
                    setShowTemplates(false);
                  }}
                  style={{
                    padding: '2px 8px',
                    border: '1px solid var(--color-border-primary)',
                    borderRadius: 'var(--radius-full)',
                    background: 'var(--color-surface-primary)',
                    cursor: 'pointer',
                    fontSize: 'var(--text-xs)',
                  }}
                >
                  {t.name}
                </button>
              ))
            )}
          </div>
        )}
        <Textarea value={draft} onChange={setDraft} rows={3} placeholder="Type a message…" />
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--space-2)' }}>
          <Button onClick={() => draft && sendMut.mutate()} disabled={!draft || sendMut.isPending}>
            {sendMut.isPending ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </div>
    </Card>
  );
}
